import {
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { DatePicker } from '@blocksuite/affine-components/date-picker';
import { createLitPortal } from '@blocksuite/affine-components/portal';
import { IS_MOBILE } from '@blocksuite/global/env';
import { flip, offset } from '@floating-ui/dom';
import { computed, signal } from '@preact/signals-core';
import { html } from 'lit';

import { dataViewI18nT, getDataViewRuntimeLocale } from '../../core/i18n.js';
import { BaseCellRenderer } from '../../core/property/index.js';
import { createFromBaseCellRenderer } from '../../core/property/renderer.js';
import { createIcon } from '../../core/utils/uni-icon.js';
import { dayOfLocalDate, localDateOfDay } from './calendar-day.js';
import {
  dateCellStyle,
  datePickerContainerStyle,
  dateTimeFooterStyle,
  dateTimeInputRowStyle,
  dateTimeInputStyle,
  dateTimePickerContainerStyle,
  dateTimeQuickButtonStyle,
  dateTimeQuickRowStyle,
  dateValueContainerStyle,
} from './cell-renderer-css.js';
import { type DatePropertyData, datePropertyModelConfig } from './define.js';

export function formatDateCellValue(
  value?: Date,
  options?: { includeTime?: boolean; locale?: string }
): string {
  if (!value) return '';
  if (!Number.isFinite(value.getTime())) return '';
  return new Intl.DateTimeFormat(
    options?.locale ?? getDataViewRuntimeLocale(),
    {
      day: '2-digit',
      hour: options?.includeTime ? '2-digit' : undefined,
      minute: options?.includeTime ? '2-digit' : undefined,
      month: '2-digit',
      year: 'numeric',
    }
  ).format(value);
}

function formatTimeInputValue(value?: Date): string {
  if (!value || !Number.isFinite(value.getTime())) return '';
  const hour = value.getHours().toString().padStart(2, '0');
  const minute = value.getMinutes().toString().padStart(2, '0');
  return `${hour}:${minute}`;
}

function mergeDatePart(date: Date, previous?: Date): Date {
  if (!previous) return date;
  const next = new Date(date);
  next.setHours(
    previous.getHours(),
    previous.getMinutes(),
    previous.getSeconds(),
    previous.getMilliseconds()
  );
  return next;
}

function mergeTimePart(value: string, previous?: Date): Date | undefined {
  if (!value) {
    if (!previous) return undefined;
    const next = new Date(previous);
    next.setHours(0, 0, 0, 0);
    return next;
  }
  const [hourRaw, minuteRaw] = value.split(':');
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return previous;
  const next = previous ? new Date(previous) : new Date();
  next.setHours(hour, minute, 0, 0);
  return next;
}

function setTime(value: Date | undefined, hour: number, minute = 0): Date {
  const next = value ? new Date(value) : new Date();
  next.setHours(hour, minute, 0, 0);
  return next;
}

export class DateCell extends BaseCellRenderer<number, number> {
  private _prevPortalAbortController: AbortController | null = null;

  private get includeTime(): boolean {
    return (this.property.data$.value as DatePropertyData).includeTime === true;
  }

  private readonly renderTimeInput = () => {
    if (!this.includeTime) return null;
    return html`<label class="${dateTimeInputRowStyle}">
      <span>${dataViewI18nT('dateCell.selectedTime')}</span>
      <input
        class="${dateTimeInputStyle}"
        type="time"
        .value=${formatTimeInputValue(this.tempValue$.value)}
        @input=${(event: Event) => {
          const input = event.currentTarget as HTMLInputElement;
          this.tempValue$.value = mergeTimePart(
            input.value,
            this.tempValue$.value
          );
        }}
      />
    </label>`;
  };

  private readonly renderQuickTimeButton = (
    label: string,
    value: Date | 'now'
  ) =>
    html`<button
      class="${dateTimeQuickButtonStyle}"
      type="button"
      @click=${() => {
        this.tempValue$.value =
          value === 'now'
            ? new Date()
            : setTime(
                this.tempValue$.value,
                value.getHours(),
                value.getMinutes()
              );
      }}
    >
      ${label}
    </button>`;

  private readonly renderDateTimePicker = (datePicker: DatePicker) => {
    if (!this.includeTime) {
      return html` <div class="${datePickerContainerStyle}">
        ${datePicker}
      </div>`;
    }
    return html`<div class="${dateTimePickerContainerStyle}">
      <div class="${dateTimeFooterStyle}">
        <div>${datePicker}</div>
        <div data-testid="date-time-controls">
          ${this.renderTimeInput()}
          <div
            class="${dateTimeQuickRowStyle}"
            role="group"
            aria-label=${dataViewI18nT('dateCell.quickTimes')}
          >
            <span>${dataViewI18nT('dateCell.quickTimes')}</span>
            ${this.renderQuickTimeButton(
              dataViewI18nT('dateCell.quickTimeMorning'),
              setTime(undefined, 9)
            )}
            ${this.renderQuickTimeButton(
              dataViewI18nT('dateCell.quickTimeNoon'),
              setTime(undefined, 12)
            )}
            ${this.renderQuickTimeButton(
              dataViewI18nT('dateCell.quickTimeEvening'),
              setTime(undefined, 17)
            )}
            ${this.renderQuickTimeButton(
              dataViewI18nT('dateCell.quickTimeNow'),
              'now'
            )}
          </div>
        </div>
      </div>
    </div>`;
  };

  private readonly openDatePicker = () => {
    if (
      this._prevPortalAbortController &&
      !this._prevPortalAbortController.signal.aborted
    )
      return;

    this.tempValue$.value = this.cellDate(this.value);

    this._prevPortalAbortController?.abort();
    const abortController = new AbortController();
    abortController.signal.addEventListener(
      'abort',
      () => {
        this.selectCurrentCell(false);
      },
      { once: true }
    );
    this._prevPortalAbortController = abortController;
    if (IS_MOBILE) {
      popMenu(popupTargetFromElement(this), {
        options: {
          title: {
            text: this.property.name$.value,
          },
          onClose: () => {
            abortController.abort();
          },
          items: [
            () =>
              html` <div class="${dateValueContainerStyle}">
                ${this.formattedTempValue$.value}
              </div>`,
            () => {
              const datePicker = new DatePicker();
              datePicker.padding = 0;
              datePicker.value = this.tempValue$.value?.getTime() ?? Date.now();
              datePicker.onChange = (date: Date) => {
                this.tempValue$.value = this.includeTime
                  ? mergeDatePart(date, this.tempValue$.value)
                  : date;
              };
              datePicker.onClear = () => {
                this.tempValue$.value = undefined;
              };
              datePicker.onEscape = () => {
                abortController.abort();
              };
              requestAnimationFrame(() => datePicker.focusDateCell());
              return this.renderDateTimePicker(datePicker);
            },
          ],
        },
      });
    } else {
      const { portal } = createLitPortal({
        abortController,
        closeOnClickAway: true,
        computePosition: {
          referenceElement: this,
          placement: 'bottom',
          middleware: [offset(10), flip()],
        },
        template: () => {
          const datePicker = new DatePicker();
          datePicker.value = this.tempValue$.value?.getTime() ?? Date.now();
          datePicker.popup = true;
          datePicker.onClear = () => {
            this.tempValue$.value = undefined;
          };
          datePicker.onChange = (date: Date) => {
            this.tempValue$.value = this.includeTime
              ? mergeDatePart(date, this.tempValue$.value)
              : date;
          };
          datePicker.onEscape = () => {
            abortController.abort();
          };
          requestAnimationFrame(() => datePicker.focusDateCell());
          return this.renderDateTimePicker(datePicker);
        },
      });
      // TODO: use z-index from variable,
      //       for now the slide-layout-modal's z-index is `1001`
      //       the z-index of popover should be higher than it
      // root.style.zIndex = 'var(--streamlain-z-index-popover)';
      portal.style.zIndex = '1002';
    }
  };

  private readonly updateValue = () => {
    const tempValue = this.tempValue$.value;
    const currentValue = this.value;
    const time = tempValue && this.storedValue(tempValue);

    if ((!time && !currentValue) || (time && time === currentValue)) {
      return;
    }

    this.valueSetNextTick(time);
    this.tempValue$.value = undefined;
  };

  tempValue$ = signal<Date | undefined>();

  /** The local `Date` the picker and the formatter show for a stored value. */
  private cellDate(value: number | null | undefined): Date | undefined {
    if (!value) return undefined;
    return this.includeTime ? new Date(value) : localDateOfDay(value);
  }

  /** The stored value of a picked `Date`: a moment, or the day it falls on. */
  private storedValue(date: Date): number {
    return this.includeTime ? date.getTime() : dayOfLocalDate(date);
  }

  format(value?: Date) {
    return formatDateCellValue(value, { includeTime: this.includeTime });
  }

  formattedTempValue$ = computed(() => {
    return this.format(this.tempValue$.value);
  });
  formattedValue$ = computed(() => {
    return (
      this.formattedTempValue$.value || this.format(this.cellDate(this.value))
    );
  });

  override afterEnterEditingMode() {
    this.openDatePicker();
  }

  override beforeExitEditingMode() {
    this.updateValue();
    this._prevPortalAbortController?.abort();
  }

  override render() {
    return html` <div class="${dateCellStyle} date">
      ${this.formattedValue$.value}
    </div>`;
  }
}

export const datePropertyConfig = datePropertyModelConfig.createPropertyMeta({
  icon: createIcon('DateTimeIcon'),
  cellRenderer: {
    view: createFromBaseCellRenderer(DateCell),
  },
});
